import { useCallback, useMemo, useState } from "react";
import type { LibraryItem } from "../../api/library";
import type { Spark } from "../../api/spark";

const MAX_TAGS = 7;

const getSparkScorer = (activeSparkTags: string[]) => {
  const tagPositions: Record<string, number> = {};
  activeSparkTags.forEach((t, idx) => {
    tagPositions[t] = idx;
  });
  return (item: LibraryItem) =>
    item.tags.reduce((prevTagScore, t) => {
      const pos = tagPositions[t] !== undefined ? tagPositions[t] : 100;
      return prevTagScore + 1 / Math.pow(pos + 1, 2);
    }, 1);
};

const overlapScorer =
  (getValues: (item: LibraryItem) => string[], filter: Set<string>) =>
  (item: LibraryItem) =>
    getValues(item).filter((v) => filter.has(v)).length * 1000 || 1;

const collectSet = (
  items: LibraryItem[],
  getValues: (item: LibraryItem) => string[],
) => {
  const res = new Set<string>();
  items.forEach((i) => getValues(i).forEach((v) => res.add(v)));
  return res;
};

const itemToType = (item: LibraryItem) => item.content.__typename;

const findBestTags = ({
  activeSparkTags,
  items,
}: {
  activeSparkTags: string[];
  items: LibraryItem[];
}) => {
  const tagCounts: Record<string, number> = {};
  const sparkTags = new Set(activeSparkTags);
  // consider only items which have at least one tag in common with selected spark
  const validItems = !sparkTags.size
    ? items
    : items.filter((item) => item.tags.some((tag) => sparkTags.has(tag)));
  validItems.forEach((item) => {
    item.tags.forEach((tag) => (tagCounts[tag] = (tagCounts[tag] || 0) + 1));
  });
  const tagsWithCounts = Object.entries(tagCounts);
  tagsWithCounts.sort((tc1, tc2) => tc2[1] - tc1[1]);
  return tagsWithCounts.slice(0, MAX_TAGS).map(([tag]) => tag);
};

/**
 * Custom React hook for managing a single tag selection state.
 *
 * This hook maintains a tag value that can be toggled on/off. When a tag is selected,
 * it returns a Set containing that tag. When toggled again with the same value, it
 * deselects the tag and returns an empty Set. This allows for single-selection filter
 * behavior in the library interface.
 *
 * @param initial - The initial tag value to set, or null for no selection
 * @returns A tuple of the currently selected tag (as a Set, empty if none) and a
 *          function that toggles the tag selection on/off.
 */
const useTag = (initial: string | null = null) => {
  const [data, setData] = useState(initial);
  const toggle = useCallback(
    (val: string) => setData((prev) => (prev === val ? null : val)),
    [],
  );
  const set = useMemo(() => {
    return data ? new Set([data]) : new Set<string>();
  }, [data]);
  return [set, toggle] as const;
};

/**
 * Owns the library's filter state and the ranking it drives. It lives above the
 * page shell because the two halves it feeds sit in different slots: the tag
 * cloud goes into the header, the ranked items into the body.
 *
 * Nothing is filtered out — selecting a tag scores matching items to the front
 * of the masonry rather than hiding the rest.
 */
export const useLibraryFilters = (items: LibraryItem[], spark: Spark) => {
  const [sparkFilter, toggleSpark] = useTag(spark.id);
  const [tagFilter, toggleTag] = useTag();
  const [typeFilter, toggleType] = useTag();
  const [categoryFilter, toggleCategory] = useTag();

  const activeSparkTags = useMemo(
    () => (sparkFilter.has(spark.id) ? spark.tags : []),
    [sparkFilter, spark],
  );

  const scorers = useMemo(() => {
    const res = [];
    if (sparkFilter.size) {
      res.push(getSparkScorer(activeSparkTags));
    }
    if (tagFilter.size) {
      res.push(overlapScorer((item) => item.tags, tagFilter));
    }
    if (typeFilter.size) {
      res.push((item: LibraryItem) =>
        typeFilter.has(itemToType(item)) ? 1000 : 1,
      );
    }
    if (categoryFilter.size) {
      res.push(overlapScorer((item) => item.categories, categoryFilter));
    }
    return res;
  }, [sparkFilter, activeSparkTags, tagFilter, typeFilter, categoryFilter]);

  const itemWithScores = useMemo(() => {
    const res = items.map((item) => ({
      item,
      score: scorers.reduce((prev, scoreFn) => prev * scoreFn(item), 1),
    }));
    res.sort((i1, i2) => i2.score - i1.score);
    return res;
  }, [items, scorers]);

  const data = useMemo(
    () => itemWithScores.map(({ item }) => item),
    [itemWithScores],
  );

  const validTypes = useMemo(
    () => collectSet(items, (i) => [itemToType(i)]),
    [items],
  );

  const validCategories = useMemo(
    () => collectSet(items, (i) => i.categories),
    [items],
  );

  const bestTags = findBestTags({ activeSparkTags, items });
  const bestTagSet = new Set(bestTags);
  const nonBestSelectedTags = [...tagFilter].filter((t) => !bestTagSet.has(t));
  const validTags = useMemo(
    () => [
      ...bestTags.slice(0, MAX_TAGS - nonBestSelectedTags.length),
      ...nonBestSelectedTags,
    ],
    [bestTags, nonBestSelectedTags],
  );

  return {
    /** The items, ranked by the current selection. */
    data,
    /** Spread onto the tag cloud in the header. */
    tagCloudProps: {
      validTags,
      tagFilter,
      toggleTag,
      validTypes,
      typeFilter,
      toggleType,
      validCategories,
      categoryFilter,
      toggleCategory,
      spark,
      sparkFilter,
      toggleSpark,
    },
    /** What each item preview needs to highlight and toggle its own tags. */
    itemContext: { tagFilter, toggleTag },
  };
};
